import { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import {
  BarChart3,
  TrendingUp,
  Wrench,
  ShoppingCart,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";

const StatisticsPage = () => {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportDate, setExportDate] = useState(new Date().toISOString().split("T")[0]);
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exportYear, setExportYear] = useState(String(new Date().getFullYear()));

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await API.get("/statistics", { params: { period } });
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period]);

  const exportCA = async (exportPeriod) => {
    setExporting(true);
    try {
      const params = { period: exportPeriod };
      if (exportPeriod === "daily") params.date = exportDate;
      if (exportPeriod === "monthly") params.month = exportMonth;
      if (exportPeriod === "annual") params.year = exportYear;
      
      const response = await API.get("/statistics/ca-export", {
        params,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `CA_${exportPeriod}_${new Date().toISOString().split("T")[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export PDF téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusLabels = {
    appareil_depose: "Déposé",
    en_attente_diagnostic: "Att. diagnostic",
    diagnostic_en_cours: "Diagnostic",
    en_attente_devis: "Att. devis",
    devis_envoye: "Devis envoyé",
    devis_accepte: "Devis accepté",
    devis_refuse: "Devis refusé",
    en_attente_pieces: "Att. pièces",
    reparation_en_cours: "Réparation",
    test_final: "Test final",
    pret_a_restituer: "Prêt",
    restitue: "Restitué",
    cloture: "Clôturé",
    refus_garantie: "Refus garantie",
    irreparable: "Irréparable",
    sans_suite: "Sans suite"
  };

  const totalSAV = stats?.sav_by_status?.reduce((sum, s) => sum + s.count, 0) || 0;

  return (
    <div className="space-y-6" data-testid="statistics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Statistiques</h1>
          <p className="text-muted-foreground">Analyse de votre activité</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Cette semaine</SelectItem>
            <SelectItem value="month">Ce mois</SelectItem>
            <SelectItem value="year">Cette année</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Devis Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Devis acceptés</p>
                <p className="text-2xl font-bold">{stats?.devis?.acceptes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Devis refusés</p>
                <p className="text-2xl font-bold">{stats?.devis?.refuses || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-yellow-100">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Devis en attente</p>
                <p className="text-2xl font-bold">{stats?.devis?.en_attente || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Taux d'acceptation</p>
                <p className="text-2xl font-bold">{stats?.devis?.taux_acceptation || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Stats Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* SAV by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Dossiers SAV par statut
            </CardTitle>
            <CardDescription>{totalSAV} dossiers au total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.sav_by_status?.map((status) => (
                <div key={status._id} className="flex items-center gap-3">
                  <div className="w-32 text-sm truncate">
                    {statusLabels[status._id] || status._id}
                  </div>
                  <div className="flex-1">
                    <Progress value={(status.count / totalSAV) * 100} className="h-2" />
                  </div>
                  <div className="w-12 text-right font-mono text-sm">{status.count}</div>
                </div>
              ))}
              {(!stats?.sav_by_status || stats.sav_by_status.length === 0) && (
                <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SAV by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Répartition garantie / hors garantie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.sav_by_type?.map((type) => {
                const total = stats.sav_by_type.reduce((sum, t) => sum + t.count, 0);
                const percentage = total > 0 ? Math.round((type.count / total) * 100) : 0;
                return (
                  <div key={type._id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium capitalize">{type._id?.replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground">{type.count} ({percentage}%)</span>
                    </div>
                    <Progress value={percentage} className="h-3" />
                  </div>
                );
              })}
              {(!stats?.sav_by_type || stats.sav_by_type.length === 0) && (
                <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Produits les plus vendus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.top_produits?.map((product, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center rounded-full">
                      {index + 1}
                    </Badge>
                    <span className="font-medium">{product._id || "Produit"}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium">{product.total_vendu} unités</p>
                    <p className="text-sm text-muted-foreground">{product.ca?.toFixed(2)}€</p>
                  </div>
                </div>
              ))}
              {(!stats?.top_produits || stats.top_produits.length === 0) && (
                <p className="text-center text-muted-foreground py-8">Aucune vente enregistrée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Day */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              CA par jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.ca_par_jour?.slice(-7).map((day) => {
                const maxCA = Math.max(...stats.ca_par_jour.map(d => d.total));
                const percentage = maxCA > 0 ? (day.total / maxCA) * 100 : 0;
                return (
                  <div key={day._id} className="flex items-center gap-3">
                    <div className="w-20 text-sm text-muted-foreground">
                      {new Date(day._id).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" })}
                    </div>
                    <div className="flex-1">
                      <div
                        className="h-6 rounded bg-primary/80 flex items-center justify-end px-2"
                        style={{ width: `${Math.max(percentage, 5)}%` }}
                      >
                        <span className="text-xs text-primary-foreground font-mono">
                          {day.total?.toFixed(0)}€
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!stats?.ca_par_jour || stats.ca_par_jour.length === 0) && (
                <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CA Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export PDF du Chiffre d'Affaires
          </CardTitle>
          <CardDescription>Téléchargez un rapport PDF détaillé de votre CA</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Daily */}
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label className="font-semibold">Journalier</Label>
              </div>
              <Input
                type="date"
                value={exportDate}
                onChange={(e) => setExportDate(e.target.value)}
                data-testid="export-date-input"
              />
              <Button
                className="w-full"
                variant="outline"
                onClick={() => exportCA("daily")}
                disabled={exporting}
                data-testid="export-daily-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export journalier
              </Button>
            </div>

            {/* Monthly */}
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label className="font-semibold">Mensuel</Label>
              </div>
              <Input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                data-testid="export-month-input"
              />
              <Button
                className="w-full"
                variant="outline"
                onClick={() => exportCA("monthly")}
                disabled={exporting}
                data-testid="export-monthly-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export mensuel
              </Button>
            </div>

            {/* Annual */}
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label className="font-semibold">Annuel</Label>
              </div>
              <Input
                type="number"
                min="2020"
                max="2030"
                value={exportYear}
                onChange={(e) => setExportYear(e.target.value)}
                data-testid="export-year-input"
              />
              <Button
                className="w-full"
                variant="outline"
                onClick={() => exportCA("annual")}
                disabled={exporting}
                data-testid="export-annual-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export annuel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatisticsPage;
